import { Component, OnDestroy, SecurityContext, inject } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import { catchError, first, map, startWith, switchMap } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService, UserOption } from '../../projects/projects.service';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import {
  Task,
  TaskDetails,
  TaskInput,
  TaskFormQuestionType,
  TaskFormQuestion,
  TaskSubFormResponse,
} from '../../shared/models/deprecated-task.model';
import { TaskStatus, TaskViewer } from '../../shared/enums/deprecated-task.enum';
import { Container } from '../../shared/models/container.model';
import { TasksRow } from '../tasks-list/tasks-list.component';
import { TasksService } from '../tasks.service';
import { getTaskStatusDisplay } from '../tasks.utils';
import { ViewerService } from '../../viewers/viewer.service';
import { OhifApp } from '../../viewers/ohifviewer/ohifviewer.utils';
import { downloadFile } from '../../shared/browser.utils';
import { ConfigService } from '../../config.service';

interface FormDataRow {
  label: string;
  key: string;
  type: TaskFormQuestionType;
  response?: string;
}

@Component({
  selector: 'task-review-dialog',
  templateUrl: './task-review-dialog.component.html',
  styleUrls: ['./task-review-dialog.component.scss'],
  standalone: false,
})
export class TaskReviewComponent implements OnDestroy {
  data = inject<{
    taskRow: TasksRow;
}>(MAT_DIALOG_DATA);
  private config = inject(ConfigService);
  private dialogRef = inject<MatDialogRef<TaskReviewComponent>>(MatDialogRef);
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);
  private tasksService = inject(TasksService);
  private notifications = inject(NotificationService);
  private projectsService = inject(ProjectsService);
  private profileService = inject(ProfileService);
  private viewers = inject(ViewerService);
  private sanitizer = inject(DomSanitizer);

  readonly annotationData = new MatTableDataSource<FormDataRow>();
  isReaderTaskAdmin = false;
  canDownloadFiles = false;
  cloneView = false;
  readonly formData = new MatTableDataSource<FormDataRow>();
  formGroup = new FormGroup({
    assignee: new FormControl(this.data.taskRow.assignee._id, [Validators.required]),
    dueDate: new FormControl(this.data.taskRow.dueDate.date, []),
    status: new FormControl(this.data.taskRow.status.value, [Validators.required]),
    tags: new FormControl(this.data.taskRow.tags || []),
  });
  isSiteContext = false;
  isSliceSpecificWorkflow = false;
  statusOptions = [
    { display: getTaskStatusDisplay(TaskStatus.Todo), value: TaskStatus.Todo },
    { display: getTaskStatusDisplay(TaskStatus.InProgress), value: TaskStatus.InProgress },
    { display: getTaskStatusDisplay(TaskStatus.Complete), value: TaskStatus.Complete },
  ];
  tagContainer: Container = null;
  taskDetails: TaskDetails = null;
  taskTagContainers = {
    include: {
      tags: [],
    },
    exclude: {
      tags: [],
    },
  };
  users: UserOption[] = [];
  fetchTaskDetails$: Subject<void> = new Subject();

  private subscriptions = {
    data: Subscription.EMPTY,
    canDownloadFiles: Subscription.EMPTY,
  };

  constructor() {
    this.formGroup.get('dueDate').setValidators([this.futureOrPristineDateValidator]);

    this.subscriptions.data = combineLatest([
      this.projectsService.getProjectUsersWithPermission(this.data.taskRow.projectId, [PermissionAction.FormResponsesManage, PermissionAction.AnnotationsManage]),
      this.fetchTaskDetails$.pipe(
        startWith(undefined as void),
        switchMap(() => this.coreApi.readerTasks.details(this.data.taskRow._id).pipe(catchError(() => {
          this.notifications.error('tasks.taskDetails.errorFetchingDetails');
          return of(null as TaskDetails);
        }))),
      ),
    ]).subscribe(([users, taskDetails]) => {
      this.users = sortBy(users.map(user => {
        return {
          ...user,
          value: user._id,
          label: `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || user.email || user._id,
        };
      }), user => user.label.toLowerCase());
      if (taskDetails === null) {
        return;
      }
      // If task details have changed after initializing, refresh the details in the parent task list
      if (this.taskDetails !== null && !isEqual(taskDetails.reader_task, this.taskDetails?.reader_task)) {
        this.tasksService.refreshTaskList$.next();
      }
      this.taskDetails = taskDetails;
      this.formGroup.reset({
        assignee: taskDetails.reader_task.assignee,
        dueDate: taskDetails.reader_task.due_date ? new Date(taskDetails.reader_task.due_date) : null,
        status: taskDetails.reader_task.status,
        tags: taskDetails.reader_task.tags,
      });
      this.taskTagContainers.include.tags = taskDetails.reader_task.parent_tag_filter?.include || [];
      this.taskTagContainers.exclude.tags = taskDetails.reader_task.parent_tag_filter?.exclude || [];
      const responses = Object.entries(taskDetails.form_response?.response_data ?? {});
      this.formData.data = (taskDetails.form?.form?.studyForm?.components || []).map(question => {
        if (responses.length === 0) {
          return null;
        }
        const responseValue = (responses.find(([key]) => key === question.key) ?? [])[1];
        const responseDisplay = this.formatResponseDisplay(responseValue, question);
        return {
          label: question.label,
          key: question.key,
          type: question.type,
          response: responseDisplay,
        } as FormDataRow;
      }).filter(row => Boolean(row?.response)); // hide rows with no response
      this.annotationData.data = uniqBy(taskDetails.annotations || [], annotation => {
        return `${annotation.data.location} ${annotation.data.toolType}`; // group annotations by label and tool type
      }).map(annotation => {
        const similarCount = (taskDetails.annotations.filter(a => annotation.data.location === a.data.location && annotation.data.toolType === a.data.toolType) || []).length;
        return {
          label: annotation.data.location,
          key: annotation.data.toolType,
          response: `${similarCount}`,
          type: null,
        } as FormDataRow;
      });
      if (!isEmpty(taskDetails.reader_task.config_overrides?.bSlices)) {
        this.isSliceSpecificWorkflow = true;
        // add empty rows to ensure the error shows just once
        if (responses.length > 0) {
          this.formData.data = [{} as FormDataRow];
        }
        if (taskDetails.annotations.length > 0) {
          this.annotationData.data = [{} as FormDataRow];
        }
      }
    });
    // TODO: figure out what the point of isSiteContext is
    // this.isSiteContext = this.oldRouter.current.name === 'index.tasks';
    this.profileService.getProfile().pipe(
      first((profile) => Boolean(profile)),
      switchMap((profile) => {
        if (this.data.taskRow.creator._id === profile._id) {
          return of(true);
        }
        return this.projectsService.getProjectPermissions(this.data.taskRow.projectId)
          .pipe(map(permissions => permissions.includes(PermissionAction.ReaderTaskAdmin)));
      }),
    ).subscribe((isReaderTaskAdmin) => {
      this.isReaderTaskAdmin = isReaderTaskAdmin;
      this.formGroup[this.isReaderTaskAdmin ? 'enable' : 'disable']();
    });
    this.projectsService.getProject(this.data.taskRow.projectId).subscribe(project => {
      this.tagContainer = { ...project, tags: (this.data.taskRow.tags || []) };
    });//

    this.subscriptions.canDownloadFiles = this.projectsService.getProjectPermissions(this.data.taskRow.projectId)
      .subscribe(projectPermissions => {
        this.canDownloadFiles = projectPermissions.includes(PermissionAction.FilesDownload);
      });

  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  viewTask(): void {
    let app;

    if (this.data.taskRow.viewer === TaskViewer.OHIFV3) {
      app = OhifApp.OhifViewerV3;
    } else if (this.data.taskRow.viewer === TaskViewer.OHIF) {
      app = OhifApp.OhifReact;
    }

    this.viewers.ohifViewerDialog({ taskId: this.data.taskRow._id, app }).subscribe(() => {
      this.fetchTaskDetails$.next();
    });
  }

  viewRelatedTasks(): void {
    /* TODO: what this do?
    this.oldRouter.go(this.isSiteContext ? 'index.tasks' : 'index.projectsManage', {
      projectTab: 'tasks',
      entityId: null,
      action: null,
      tab: null,
      view: 'all',
      containerId: this.taskDetails?.reader_task?.parent.id,
      project: this.isSiteContext ? this.taskDetails?.reader_task?.parents.project : null,
      protocol: this.taskDetails?.reader_task?.protocol_id,
    }, { notify: false });
    this.dialogRef.close();
    */
  }

  submit(): void {
    const updatedTask: Task = { ...this.taskDetails.reader_task };
    updatedTask.assignee = this.formGroup.get('assignee').value;
    const updatedDate = new Date(this.formGroup.get('dueDate').value);
    updatedTask.due_date = this.formGroup.get('dueDate').value ? updatedDate.toISOString() : null;
    updatedTask.status = this.formGroup.get('status').value;
    updatedTask.tags = this.formGroup.get('tags').value;
    this.coreApi.readerTasks.put(updatedTask._id, updatedTask).subscribe({
      next: () => {
        this.notifications.success('tasks.taskEdit.success');
        this.tasksService.refreshTaskList$.next();
        this.dialogRef.close();
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('tasks.taskEdit.error');
      },
    });
  }

  showCloneView(shouldShow: boolean): void {
    this.cloneView = shouldShow;
    if (shouldShow) {
      this.formGroup.controls.status.setValidators([]);
      this.formGroup.controls.dueDate.setValidators([this.futureDateValidator]);
      this.formGroup.reset({ tags: this.tagContainer.tags });
    } else {
      // restore task details
      this.formGroup.reset({
        assignee: this.taskDetails.reader_task.assignee,
        dueDate: this.taskDetails.reader_task.due_date ? new Date(this.taskDetails.reader_task.due_date) : null,
        status: this.taskDetails.reader_task.status,
        tags: this.taskDetails.reader_task.tags,
      });
      this.formGroup.controls.status.setValidators([Validators.required]);
      this.formGroup.controls.dueDate.setValidators([this.futureOrPristineDateValidator]);
    }
  }

  createClone(): void {
    const sourceTask = pick(this.taskDetails.reader_task, ['parent', 'viewer', 'viewer_config_id', 'info', 'config_overrides', 'task_type', 'tags', 'protocol_id']);
    const cloneForm = this.formGroup.getRawValue();
    const task: TaskInput = {
      ...sourceTask, ...{
        assignee: cloneForm.assignee,
        due_date: cloneForm.dueDate?.toISOString ? cloneForm.dueDate.toISOString() : null,
        status: TaskStatus.Todo,
        tags: cloneForm.tags,
      },
    };
    this.coreApi.readerTasks.post(task).subscribe({
      next: () => {
        this.notifications.success('tasks.cloneCreate.success');
        this.showCloneView(false);
        this.tasksService.refreshTaskList$.next();
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('tasks.cloneCreate.error');
      },
    });
  }

  deleteTask(): void {
    this.coreApi.readerTasks.delete(this.data.taskRow._id).subscribe({
      next: () => {
        this.notifications.success('tasks.deleteOne.success');
        this.tasksService.refreshTaskList$.next();
        this.dialogRef.close();
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('tasks.deleteOne.error');
      },
    });
  }


  downloadTaskFiles(task: TasksRow): void {
    if (task.container.type === 'file') {
      this.coreApi.files.ticket(task.container._id).subscribe({
        next: ({ ticket, filename }) => {
          downloadFile(filename, this.config.getApiUrl(`/api/files/${task.container._id}/download?ticket=${ticket}`));
        },
        error: error => {
          this.logger.error(error);
          this.notifications.error('download.files.error');
        },
      });
    } else {
      this.coreApi.downloads.createReadTaskTicket({
        optional: true,
        nodes: [{ level: task.container.type, _id: task.container._id }],
      }, task.taskId).subscribe({
        next: ({ filename, ticket }) => {
          downloadFile(filename, this.config.getApiUrl(`/api/download?ticket=${ticket}&format=zip`));
        },
        error: error => {
          this.logger.error(error);
          this.notifications.error('download.files.error');
        },
      });
    }
  }

  formatResponseDisplay(response: string | string[] | TaskSubFormResponse, question: TaskFormQuestion): string {
    let responseValue: string | string[];
    if (typeof response === 'object' && !Array.isArray(response) && response.value !== undefined) {
      // it seems like annotations are being saved along side the actual response :/
      // reassign to just the value if it exists
      responseValue = response.value;
    } else {
      responseValue = response as string | string[];
    }
    let responseDisplay: string;
    if ([TaskFormQuestionType.Dropdown, TaskFormQuestionType.Radio].includes(question.type)) {
      if (Array.isArray(responseValue)) {
        responseDisplay = responseValue.map(response => {
          const responseOption = (question.values ?? []).find(val => val.value === response);
          return responseOption ? responseOption.label : response;
        }).join(', ');
      } else {
        const responseOption = (question.values ?? []).find(val => val.value === responseValue);
        responseDisplay = responseOption ? responseOption.label : responseValue;
      }
    } else if (question.type === TaskFormQuestionType.SelectBoxes) {
      const responses = (responseValue as string[] ?? []).map(response => {
        const responseOption = (question.values ?? []).find(val => val.value === response);
        return responseOption ? responseOption.label : responseValue;
      });
      responseDisplay = responses.join(', ');
    } else if ([TaskFormQuestionType.Text, TaskFormQuestionType.TextArea, TaskFormQuestionType.TextField].includes(question.type)) {
      responseDisplay = (responseValue as string ?? '').split('\n').slice(0, 500).join('\n');
    } else {
      responseDisplay = Array.isArray(responseValue) ? responseValue.join(', ') : responseValue;
    }
    return this.sanitizer.sanitize(SecurityContext.HTML, responseDisplay);
  }

  private futureDateValidator: ValidatorFn = control => {
    if (!control.value || control.pristine) {
      return null;
    }
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return control.value < now ? { pastDate: true } : null;
  };

  private futureOrPristineDateValidator: ValidatorFn = control => {
    if (!control.value || control.pristine) {
      return null;
    }
    // Allow the original due_date regardless of when it is
    const pristineDueDate = (this?.taskDetails?.reader_task?.due_date || '').slice(0, 10);
    if (pristineDueDate && pristineDueDate === control.value.toISOString().slice(0, 10)) {
      return null;
    }
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return control.value < now ? { pastDate: true } : null;
  };
}
